import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useTranslation } from 'react-i18next'
import type {
  DocumentationDryRunResult,
  DocumentationSyncLog,
  DocumentationSyncStatusResult,
} from '@/services/admin/documentationService'

interface DocumentationContentSyncTabProps {
  syncStatusOptions: Array<{ value: string; label: string }>
  syncStatusQuery: {
    isLoading: boolean
    data?: DocumentationSyncStatusResult
  }
  syncLogsQuery: {
    isLoading: boolean
    isFetching: boolean
    refetch: () => void
  }
  syncRows: DocumentationSyncLog[]
  syncPagination?: {
    page: number
    pages: number
    total: number
  }
  syncPage: number
  syncStatus: string
  syncSourceRef: string
  syncDeliveryId: string
  replayPayload: string
  replaySourceRef: string
  replayDeliveryId: string
  isReplayExpanded: boolean
  dryRunResult: DocumentationDryRunResult | null
  syncConfigReady: boolean
  syncConfigLoading: boolean
  syncConfigError?: string | null
  publishedVersionOptions: Array<{ value: string; label: string }>
  forcePublishedVersionCode: string
  quickSyncPending: boolean
  rebuildPending: boolean
  forcePublishedSyncPending: boolean
  replayPending: boolean
  dryRunPending: boolean
  onOpenQuickSyncConfirm: () => void
  onOpenRebuildConfirm: () => void
  onOpenForcePublishedSyncConfirm: () => void
  onSetForcePublishedVersionCode: (value: string) => void
  onSetReplayExpanded: (value: boolean | ((prev: boolean) => boolean)) => void
  onSetReplayPayload: (value: string) => void
  onSetReplaySourceRef: (value: string) => void
  onSetReplayDeliveryId: (value: string) => void
  onDryRun: () => void
  onReplay: () => void
  onSetSyncStatus: (value: string) => void
  onSetSyncSourceRef: (value: string) => void
  onSetSyncDeliveryId: (value: string) => void
  onSetSyncPage: (value: number | ((current: number) => number)) => void
  formatDateTime: (value?: string | null) => string
  statusColor: (status: string) => string
}

export function DocumentationContentSyncTab({
  syncStatusOptions,
  syncStatusQuery,
  syncLogsQuery,
  syncRows,
  syncPagination,
  syncPage,
  syncStatus,
  syncSourceRef,
  syncDeliveryId,
  replayPayload,
  replaySourceRef,
  replayDeliveryId,
  isReplayExpanded,
  dryRunResult,
  syncConfigReady,
  syncConfigLoading,
  syncConfigError,
  publishedVersionOptions,
  forcePublishedVersionCode,
  quickSyncPending,
  rebuildPending,
  forcePublishedSyncPending,
  replayPending,
  dryRunPending,
  onOpenQuickSyncConfirm,
  onOpenRebuildConfirm,
  onOpenForcePublishedSyncConfirm,
  onSetForcePublishedVersionCode,
  onSetReplayExpanded,
  onSetReplayPayload,
  onSetReplaySourceRef,
  onSetReplayDeliveryId,
  onDryRun,
  onReplay,
  onSetSyncStatus,
  onSetSyncSourceRef,
  onSetSyncDeliveryId,
  onSetSyncPage,
  formatDateTime,
  statusColor,
}: DocumentationContentSyncTabProps) {
  const { t } = useTranslation()

  return (
    <VStack align="stretch" spacing={4}>
      {!syncConfigReady ? (
        <Alert status={syncConfigLoading ? 'info' : 'error'} borderRadius="md">
          <AlertIcon />
          <Text>
            {syncConfigLoading
              ? t('documentationContent.sync.loadingRuntimeSettings')
              : syncConfigError ?? t('documentationContent.sync.runtimeSettingsNotLoaded')}
          </Text>
        </Alert>
      ) : null}

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.sync.quickSyncTitle')}
        </Text>
        <VStack align="stretch" spacing={3}>
          <HStack spacing={3} flexWrap="wrap">
            <Button
              colorScheme="blue"
              onClick={onOpenQuickSyncConfirm}
              isLoading={quickSyncPending}
              isDisabled={!syncConfigReady || rebuildPending}
            >
              {t('documentationContent.sync.quickSync')}
            </Button>
            <Button
              colorScheme="red"
              variant="outline"
              onClick={onOpenRebuildConfirm}
              isLoading={rebuildPending}
              isDisabled={!syncConfigReady || quickSyncPending}
            >
              {t('documentationContent.sync.rebuildFromBranch')}
            </Button>
          </HStack>
          <Box borderWidth="1px" borderRadius="md" p={3}>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              {t('documentationContent.sync.forcePublishedTitle')}
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Box w="240px">
                <DropdownSelect
                  size="sm"
                  value={forcePublishedVersionCode}
                  placeholder={t('documentationContent.sync.selectPublishedVersion')}
                  options={publishedVersionOptions}
                  onChange={onSetForcePublishedVersionCode}
                />
              </Box>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={onOpenForcePublishedSyncConfirm}
                isLoading={forcePublishedSyncPending}
                isDisabled={
                  !syncConfigReady
                  || quickSyncPending
                  || rebuildPending
                  || !forcePublishedVersionCode
                }
              >
                {t('documentationContent.sync.forceSyncPublished')}
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={2}>
              {t('documentationContent.sync.forcePublishedHint')}
            </Text>
          </Box>
          <Text fontSize="xs" color="gray.500">
            {t('documentationContent.sync.quickRebuildHint')}
          </Text>
          <Box borderWidth="1px" borderRadius="md" p={3}>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              {t('documentationContent.sync.latestSyncStatus')}
            </Text>
            {syncStatusQuery.isLoading ? (
              <HStack>
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.500">
                  {t('documentationContent.sync.loadingLatestStatus')}
                </Text>
              </HStack>
            ) : syncStatusQuery.data?.latest ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                <Text fontSize="sm">
                  <Text as="span" color="gray.500">
                    {t('documentationContent.sync.event')}:
                  </Text>{' '}
                  {syncStatusQuery.data.latest.event}
                </Text>
                <Text fontSize="sm">
                  <Text as="span" color="gray.500">
                    {t('common.status')}:
                  </Text>{' '}
                  {syncStatusQuery.data.latest.status}
                </Text>
                <Text fontSize="sm">
                  <Text as="span" color="gray.500">
                    {t('documentationContent.sync.sourceRef')}:
                  </Text>{' '}
                  {syncStatusQuery.data.latest.sourceRef ?? t('common.na')}
                </Text>
                <Text fontSize="sm">
                  <Text as="span" color="gray.500">
                    {t('documentationContent.sync.finished')}:
                  </Text>{' '}
                  {formatDateTime(syncStatusQuery.data.latest.finishedAt)}
                </Text>
              </SimpleGrid>
            ) : (
              <Text fontSize="sm" color="gray.500">
                {t('documentationContent.sync.noSyncStatus')}
              </Text>
            )}
          </Box>
        </VStack>
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <HStack justify="space-between" align="center">
          <Text fontWeight="semibold">{t('documentationContent.sync.replaySync')}</Text>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={
              isReplayExpanded
                ? t('documentationContent.sync.collapseReplaySync')
                : t('documentationContent.sync.expandReplaySync')
            }
            icon={isReplayExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            onClick={() => onSetReplayExpanded((prev) => !prev)}
          />
        </HStack>
        {isReplayExpanded ? (
          <VStack align="stretch" spacing={3} mt={3}>
            <Textarea
              minH="220px"
              value={replayPayload}
              onChange={(event) => onSetReplayPayload(event.target.value)}
              placeholder={t('documentationContent.sync.githubPayloadPlaceholder')}
              fontFamily="mono"
              fontSize="sm"
            />
            <HStack spacing={3} align="stretch" flexWrap="wrap">
              <Input
                maxW="320px"
                placeholder={t('documentationContent.sync.sourceRefPlaceholder')}
                value={replaySourceRef}
                onChange={(event) => onSetReplaySourceRef(event.target.value)}
              />
              <Input
                maxW="260px"
                placeholder={t('documentationContent.sync.deliveryIdPlaceholder')}
                value={replayDeliveryId}
                onChange={(event) => onSetReplayDeliveryId(event.target.value)}
              />
              <Button
                colorScheme="purple"
                onClick={onDryRun}
                isLoading={dryRunPending}
                isDisabled={!syncConfigReady || replayPending}
              >
                {t('documentationContent.sync.runDryRun')}
              </Button>
              <Button
                colorScheme="blue"
                variant="outline"
                onClick={onReplay}
                isLoading={replayPending}
                isDisabled={!syncConfigReady || dryRunPending}
              >
                {t('documentationContent.sync.replayApply')}
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {t('documentationContent.sync.replayHint')}
            </Text>

            {dryRunResult ? (
              <Box borderWidth="1px" borderRadius="md" p={3}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                  <Text><Text as="span" color="gray.500">{t('documentationContent.sync.ignored')}:</Text> {dryRunResult.ignored ? t('common.yes') : t('common.no')}</Text>
                  <Text><Text as="span" color="gray.500">{t('documentationContent.sync.reason')}:</Text> {dryRunResult.reason ?? t('common.na')}</Text>
                  <Text><Text as="span" color="gray.500">{t('documentationContent.sync.matched')}:</Text> {dryRunResult.summary.matchedChangedFiles}</Text>
                  <Text><Text as="span" color="gray.500">{t('documentationContent.sync.upsertPreview')}:</Text> {dryRunResult.summary.upsertedPages}</Text>
                  <Text><Text as="span" color="gray.500">{t('documentationContent.sync.removePreview')}:</Text> {dryRunResult.summary.removedPages}</Text>
                  <Text><Text as="span" color="gray.500">{t('documentationContent.sync.touchedVersions')}:</Text> {dryRunResult.summary.touchedVersions}</Text>
                  <Text><Text as="span" color="gray.500">{t('documentationContent.sync.previewWarnings')}:</Text> {dryRunResult.preview.warnings.length}</Text>
                </SimpleGrid>
              </Box>
            ) : null}
          </VStack>
        ) : null}
      </Box>

      <Divider />

      <HStack spacing={3} flexWrap="wrap">
        <Box w="220px">
          <DropdownSelect
            size="sm"
            value={syncStatus}
            options={syncStatusOptions}
            onChange={(value) => {
              onSetSyncStatus(value)
              onSetSyncPage(1)
            }}
          />
        </Box>
        <Input
          maxW="300px"
          placeholder={t('documentationContent.sync.filterBySourceRef')}
          value={syncSourceRef}
          onChange={(event) => {
            onSetSyncSourceRef(event.target.value)
            onSetSyncPage(1)
          }}
        />
        <Input
          maxW="220px"
          placeholder={t('documentationContent.sync.filterByDeliveryId')}
          value={syncDeliveryId}
          onChange={(event) => {
            onSetSyncDeliveryId(event.target.value)
            onSetSyncPage(1)
          }}
        />
        <RefreshIconButton
          label={t('documentationContent.sync.refreshSyncLogs')}
          onClick={syncLogsQuery.refetch}
          isLoading={syncLogsQuery.isFetching}
        />
      </HStack>

      {syncLogsQuery.isLoading ? (
        <HStack py={8} justify="center">
          <Spinner />
          <Text>{t('documentationContent.sync.loadingSyncLogs')}</Text>
        </HStack>
      ) : (
        <>
          <Box overflowX="auto" borderWidth="1px" borderRadius="md">
            <Table size="sm" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <Thead>
                <Tr>
                  <Th w="110px">{t('documentationContent.sync.started')}</Th>
                  <Th w="110px">{t('documentationContent.sync.finished')}</Th>
                  <Th w="80px">{t('common.status')}</Th>
                  <Th w="130px">{t('documentationContent.sync.deliveryId')}</Th>
                  <Th w="120px">{t('documentationContent.sync.sourceRef')}</Th>
                  <Th w="130px">{t('documentationContent.sync.event')}</Th>
                  <Th w="160px">{t('documentationContent.sync.summary')}</Th>
                  <Th w="320px">{t('common.error')}</Th>
                  <Th w="130px">{t('documentationContent.sync.operator')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {syncRows.map((row) => (
                  <Tr key={row.id}>
                    <Td>
                      <Text fontSize="xs" whiteSpace="normal">
                        {formatDateTime(row.startedAt)}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="xs" whiteSpace="normal">
                        {formatDateTime(row.finishedAt)}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={statusColor(row.status)}>
                        {t(`documentationContent.syncStatuses.${row.status}`, {
                          defaultValue: row.status,
                        })}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="xs" whiteSpace="normal" wordBreak="break-all" title={row.deliveryId ?? ''}>
                        {row.deliveryId ?? t('common.na')}
                      </Text>
                    </Td>
                    <Td>
                      <Text
                        fontSize="xs"
                        whiteSpace="normal"
                        wordBreak="break-all"
                        title={row.sourceRef ?? ''}
                      >
                        {row.sourceRef ?? t('common.na')}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                        {row.event || row.action}
                      </Text>
                    </Td>
                    <Td>
                      {row.summary ? (
                        <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                          {t('documentationContent.sync.summaryInline', {
                            matched: row.summary.matchedChangedFiles,
                            upserted: row.summary.upsertedPages,
                            removed: row.summary.removedPages,
                            versions: row.summary.touchedVersions,
                          })}
                        </Text>
                      ) : (
                        <Text color="gray.500" fontSize="xs">{t('common.na')}</Text>
                      )}
                    </Td>
                    <Td>
                      <Text
                        color={row.errorMessage ? 'red.500' : 'gray.500'}
                        fontSize="xs"
                        whiteSpace="normal"
                        wordBreak="break-word"
                      >
                        {row.errorMessage ?? row.reason ?? t('common.na')}
                      </Text>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">{row.adminUser.username ?? row.adminUser.id}</Text>
                        <Text fontSize="xs" color="gray.500">{row.adminUser.email ?? t('common.na')}</Text>
                      </VStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <HStack justify="space-between">
            <Text color="gray.600" fontSize="sm">
              {t('documentationContent.sync.paginationSummary', {
                page: syncPagination?.page ?? syncPage,
                pages: syncPagination?.pages ?? 1,
                total: syncPagination?.total ?? 0,
              })}
            </Text>
            <HStack>
              <Button
                size="sm"
                onClick={() => onSetSyncPage((current) => Math.max(1, current - 1))}
                isDisabled={(syncPagination?.page ?? syncPage) <= 1}
              >
                {t('common.previous')}
              </Button>
              <Button
                size="sm"
                onClick={() => onSetSyncPage((current) => current + 1)}
                isDisabled={(syncPagination?.page ?? syncPage) >= (syncPagination?.pages ?? 1)}
              >
                {t('common.next')}
              </Button>
            </HStack>
          </HStack>
        </>
      )}
    </VStack>
  )
}
